import React, { useState, useMemo } from 'react';
import { Form, Row, Col, DatePicker, Button, Table, Input, Select, Checkbox } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { fetchAllRoutesByDate, fetchBusCompanyList } from '../actions/schedule.actions';
import { loadingAction } from '../actions/loading-actions';
import { useDispatch } from 'react-redux';
import { getFormattedTime, getAPIDateFormat } from '../utils/date.utils';

export const OtaSearchPage = () => {

    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [scheduleData, setScheduleData] = useState([]);
    const [operatorBased, setOperatorBased] = useState(false);
    const [operatorList, setOperatorList] = useState([]);

    const onSuccess = (data) => {
        dispatch(loadingAction(false));
        setScheduleData(data);
    }

    const onFailure = () => {
        dispatch(loadingAction(false));
    }

    const onOperatorListSuccess = (operators) => {
        dispatch(loadingAction(false));
        setOperatorList(operators);
    }

    const onOperatorBasedChange = (e) => {
        const checked = e.target.checked;
        setOperatorBased(checked);
        form.setFieldsValue({ operatorCode: undefined });
        if (checked && operatorList.length === 0) {
            dispatch(loadingAction(true));
            fetchBusCompanyList(onOperatorListSuccess, onFailure);
        }
    }

    const onFinish = (values) => {
        let selectedDate = getAPIDateFormat(values.selectedDate);
        dispatch(loadingAction(true));
        if (operatorBased && values.operatorCode) {
            const selectedOperator = operatorList.find(operator => operator.operatorCode === values.operatorCode);
            const region = selectedOperator ? selectedOperator.region : null;
            fetchAllRoutesByDate(selectedDate, onSuccess, onFailure, values.operatorCode, region);
        } else {
            fetchAllRoutesByDate(selectedDate, onSuccess, onFailure);
        }
    }

    // Extract unique operator names from scheduleData
    const operatorNames = useMemo(() => {
        if (!scheduleData || scheduleData.length === 0) return [];
        const uniqueOperators = [...new Set(scheduleData.map(item => item.operator_name).filter(Boolean))];
        return uniqueOperators.sort();
    }, [scheduleData]);

    const columns = [
        {
            title: 'Sl.no',
            dataIndex: 'index',
            key: 'index',
            render: (text, record, index) => index + 1
        },
        {
            title: 'Operator Name',
            dataIndex: 'operator_name',
            key: 'operator_name',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8, width:300}}>
                    <Select
                        showSearch
                        placeholder="Search or select operator name"
                        value={selectedKeys[0]}
                        onChange={value => {
                            setSelectedKeys(value ? [value] : []);
                            confirm();
                        }}
                        onSearch={() => {
                            // Search is handled by filterOption
                        }}
                        filterOption={(input, option) => {
                            const searchText = input.toLowerCase().trim();
                            const optionText = option.children.toLowerCase();
                            return optionText.includes(searchText);
                        }}
                        optionFilterProp="children"
                        style={{ width: '100%', marginBottom: 8, display: 'block' }}
                        allowClear
                        dropdownMatchSelectWidth={false}
                    >
                        {operatorNames.map(name => (
                            <Select.Option key={name} value={name}>
                                {name}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button onClick={() => clearFilters()} size="small" style={{ width: '100%' }}>
                        Reset
                    </Button>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value, record) =>
                record.operator_name
                    ? record.operator_name.toLowerCase().includes(value.toLowerCase())
                    : '',
        },
        {
            title: 'Origin',
            dataIndex: 'origin',
            key: 'origin',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search origin"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90, marginRight: 8 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value, record) =>
                record.origin
                    ? record.origin.toLowerCase().includes(value.toLowerCase())
                    : '',
        },
        {
            title: 'Destination',
            dataIndex: 'destination',
            key: 'destination',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Search destination"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => confirm()}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90, marginRight: 8 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </div>
            ),
            filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value, record) =>
                record.destination
                    ? record.destination.toLowerCase().includes(value.toLowerCase())
                    : '',
        },
        {
            title: 'Departure Time',
            dataIndex: 'departure_time',
            key: 'departure_time',
            render: (text, record) => getFormattedTime(text)
        },
        {
            title: 'Arrival Time',
            dataIndex: 'arrival_time',
            key: 'arrival_time',
            render: (text, record) => getFormattedTime(text)
        }
    ]

    return (
        <div className='report-inner-div'>
            <Form
                onFinish={onFinish}
                form={form}
            >
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col offset={1} span={24}>
                        <h2>OTA Search</h2>
                    </Col>
                    <Row gutter={[16,16]} align="middle">
                        <Col>
                            <Form.Item name="selectedDate" label="Select Date">
                                <DatePicker />
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item name="operatorBased" valuePropName="checked">
                                <Checkbox onChange={onOperatorBasedChange}>Operator Based</Checkbox>
                            </Form.Item>
                        </Col>
                        {operatorBased &&
                            <Col>
                                <Form.Item name="operatorCode" label="Select Operator">
                                    <Select
                                        showSearch
                                        placeholder="Select operator"
                                        style={{ width: 240 }}
                                        optionFilterProp="children"
                                        filterOption={(input, option) =>
                                            option.children.toLowerCase().includes(input.toLowerCase().trim())
                                        }
                                        allowClear
                                    >
                                        {operatorList.map(operator => (
                                            <Select.Option key={operator.operatorCode} value={operator.operatorCode}>
                                                {operator.operatorName}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        }
                        <Col>
                            <Form.Item>
                                <Button htmlType="submit">Search</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Row>
            </Form>
            {scheduleData.length > 0 &&
                <Table dataSource={scheduleData} columns={columns} />
            }
        </div>
    )
}